import { upperFDI, lowerFDI, type FDI_Notation } from '@/common/FDI_Notation';

export type ConstraintAxisSet = {
  x: number;
  y: number;
  z: number;
};

export type ToothConstraintValues = {
  translationLimit: ConstraintAxisSet;
  rotationLimitDeg: ConstraintAxisSet;
};

const TEETH_FDI: FDI_Notation[] = [...upperFDI, ...lowerFDI];

const INCLINATION_DEG: number[] = [
  1.5, 1.5, 1.5, 1, 1, 1.5, 1, 1, 1, 1, 1.5, 1, 1, 1.5, 1.5, 1.5, 1.5, 1.5, 1.5, 1.5, 1.5, 1.5, 1.5, 1, 1, 1, 1, 1.5, 1.5, 1.5, 1.5,
  1.5,
];

const ANGULATION_DEG: number[] = [
  1, 1, 1, 1.5, 1.5, 0.5, 1, 1, 1, 1, 0.5, 1.5, 1.5, 1, 1, 1, 1, 1, 1, 1.5, 1.5, 1.5, 1.5, 1.5, 1.5, 1.5, 1.5, 1.5, 1.5, 1, 1, 1,
];

const ROTATION_DEG: number[] = [
  1, 1, 1, 2, 2, 1.5, 2, 2, 2, 2, 1.5, 2, 2, 1, 1, 1, 2, 2, 2, 2, 2, 1.5, 2, 2, 2, 2, 1.5, 2, 2, 2, 2, 2,
];

const LEFT_RIGHT_MM: number[] = Array(32).fill(0.2);

const EXTRUSION_INTRUSION_MM: number[] = [
  0.2, 0.2, 0.2, 0.1, 0.1, 0.1, 0.2, 0.2, 0.2, 0.2, 0.1, 0.1, 0.1, 0.2, 0.2, 0.2, 0.2, 0.2, 0.2, 0.1, 0.1, 0.2, 0.1, 0.1, 0.1, 0.1, 0.2,
  0.1, 0.1, 0.2, 0.2, 0.2,
];

const FORWARD_BACKWARD_MM: number[] = [
  0.2, 0.2, 0.2, 0.1, 0.2, 0.2, 0.2, 0.2, 0.2, 0.2, 0.2, 0.2, 0.1, 0.2, 0.2, 0.2, 0.2, 0.2, 0.2, 0.2, 0.2, 0.2, 0.2, 0.2, 0.2, 0.2,
  0.2, 0.2, 0.2, 0.2, 0.2, 0.2,
];

const buildConstraints = () => {
  const entries = TEETH_FDI.map((tooth, index) => [
    tooth,
    {
      translationLimit: {
        x: LEFT_RIGHT_MM[index],
        y: EXTRUSION_INTRUSION_MM[index],
        z: FORWARD_BACKWARD_MM[index],
      },
      // Axes are the tooth's orthodontic frame: X mesiodistal, Y long axis,
      // Z buccolingual. Tipping about X is inclination (torque), spinning
      // about the long axis Y is rotation, tipping about Z is angulation.
      rotationLimitDeg: {
        x: INCLINATION_DEG[index],
        y: ROTATION_DEG[index],
        z: ANGULATION_DEG[index],
      },
    } satisfies ToothConstraintValues,
  ] as const);

  return new Map<FDI_Notation, ToothConstraintValues>(entries);
};

export const DEFAULT_TREATMENT_CONSTRAINTS = buildConstraints();

export const DEFAULT_BEZIER_POINTS: [number, number, number, number] = [0, 0, 1, 1];

export const DEFAULT_PROGRESSION_CURVE = Array(10).fill(100);

export const cloneConstraintsMap = (map: Map<FDI_Notation, ToothConstraintValues>) => {
  const clone = new Map<FDI_Notation, ToothConstraintValues>();
  map.forEach((value, key) => {
    clone.set(key, {
      translationLimit: { ...value.translationLimit },
      rotationLimitDeg: { ...value.rotationLimitDeg },
    });
  });
  return clone;
};
